import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { sellService } from '@/api/services/SellService';
import { getApiMessage, isServiceUnavailable } from '@/api/errors';
import { useProfile } from '@/hooks/useProfile';
import { useRequiredData } from '@/hooks/useRequiredData';
import { ROUTES } from '@/routes/paths';
import { useAuth } from '@/stores/authContext';

export const SALE_TREE_QUERY_KEY = ['saleTree'] as const;

// Дерево моих офферов на продажу (server-state). Включено только при авторизации.
export function useSaleTree() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: SALE_TREE_QUERY_KEY,
    queryFn: ({ signal }) => sellService.getSaleTree(signal),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}

/** Параметры группы офферов: базис и культура. */
export interface SaleGroupQuery {
  readonly address: string;
  readonly addressTitle: string;
  readonly goods: string;
}

// Офферы-лоты культуры на базисе. Запрос включён только при валидных параметрах.
export function useSaleOffers(query: SaleGroupQuery | null) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['saleOffers', query?.address, query?.addressTitle, query?.goods] as const,
    queryFn: ({ signal }) =>
      sellService.getMySaleOffers(
        {
          address: query!.address,
          addressTitle: query!.addressTitle,
          parentGoodsType: query!.goods,
        },
        signal,
      ),
    enabled: isAuthenticated && query !== null,
    staleTime: 60_000,
  });
}

// Все лоты базиса (по всем культурам), сгруппированные по культуре — для карточек в списке «Продам».
export function useSaleOffersByBasis(address: string, addressTitle: string) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['saleOffersByBasis', address, addressTitle] as const,
    queryFn: ({ signal }) => sellService.getMySaleOffersByBasis({ address, addressTitle }, signal),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}

// Статусы торговой сессии биржи (окно + «открыто сейчас»). Окно смещается по времени — периодически
// обновляем, чтобы кнопка «Опубликовать на бирже» вовремя становилась доступной/недоступной.
export function useExchangeSession() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['exchangeSession'] as const,
    queryFn: ({ signal }) => sellService.getExchangeSession(signal),
    enabled: isAuthenticated,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

// Биржевые состояния набора офферов (id → {status, reason}) для бейджей/действий. Ключ — отсортированные
// id (стабильность кэша независимо от порядка). Запрос включён только при непустом списке.
export function useExchangeStatuses(ids: readonly number[]) {
  const { isAuthenticated } = useAuth();
  const sortedIds = [...ids].sort((a, b) => a - b);
  return useQuery({
    queryKey: ['exchangeStatuses', sortedIds] as const,
    queryFn: ({ signal }) => sellService.getExchangeStatuses(sortedIds, signal),
    enabled: isAuthenticated && sortedIds.length > 0,
    staleTime: 30_000,
  });
}

// Ограничения биржевого предложения (культура/качество/объём). Статичная конфигурация — держим долго.
export function useExchangeConstraints() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['exchangeConstraints'] as const,
    queryFn: ({ signal }) => sellService.getExchangeConstraints(signal),
    enabled: isAuthenticated,
    staleTime: 30 * 60_000,
  });
}

// Ориентир цены биржевого оффера (лучшая нетто-FCA у ворот + терминал). Включён только для биржевого оффера.
// Кэш живёт минуту: BID стаканов меняются в ходе торгов, но каждый рендер дёргать эндпоинт не нужно.
export function useExchangeOrientation(orderId: number | null, enabled: boolean) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['exchangeOrientation', orderId] as const,
    queryFn: ({ signal }) => sellService.getExchangeOrientation(orderId!, signal),
    enabled: isAuthenticated && enabled && orderId != null && !Number.isNaN(orderId),
    staleTime: 60_000,
  });
}

// Действия над лотом (пауза/возобновление/удаление + биржевые публикация/снятие) со сбросом кэша и уведомлениями —
// общий хук для списка «Продать» и страницы предложения. Подтверждения — через modals.
export function useSellOfferActions() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Список «Продать» строится по ключу `saleOffersByBasis`, страница предложения — по `saleOffer`, состояние на бирже —
  // по `exchangeStatuses`. Действие считается завершённым, когда обновились именно они: до этого кнопки не предлагают
  // то же действие ещё раз. Дерево базисов и лучшие цены обновляются в фоне — ждать их незачем.
  const refreshInBackground = (): void => {
    void queryClient.invalidateQueries({ queryKey: SALE_TREE_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['saleOffers'] });
    void queryClient.invalidateQueries({ queryKey: ['bestOffers'] });
  };
  const invalidate = (): Promise<unknown> => {
    refreshInBackground();
    return Promise.all(
      [['saleOffer'], ['saleOffersByBasis'], ['exchangeStatuses']].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );
  };
  // Ошибка тоже обновляет данные: лот мог уйти на биржу или в сделку, и прежнее действие уже неуместно.
  const onError = (err: unknown): void => {
    void invalidate();
    notifications.show({
      color: 'red',
      message: isServiceUnavailable(err) ? t('auth.errors.unavailable') : (getApiMessage(err) ?? t('auth.errors.generic')),
    });
  };
  const succeed = (message: string) => async (): Promise<void> => {
    await invalidate();
    notifications.show({ color: 'lime', message: t(message) });
  };

  const suspend = useMutation({
    mutationFn: (orderId: number) => sellService.suspendOffer(orderId),
    onSuccess: succeed('app.sell.lot.suspended'),
    onError,
  });
  const unsuspend = useMutation({
    mutationFn: (orderId: number) => sellService.unsuspendOffer(orderId),
    onSuccess: succeed('app.sell.lot.resumed'),
    onError,
  });
  const removeOffer = useMutation({
    mutationFn: (orderId: number) => sellService.deleteOffer(orderId),
    onSuccess: async (_data, orderId) => {
      // Удалённый лот и его состояние на бирже не перезапрашиваем: открытая страница предложения получила бы
      // «не найдено» до перехода в список. Кэш помечаем устаревшим, чтобы возврат назад по истории не показал удалённое.
      void queryClient.invalidateQueries({ queryKey: ['saleOffer', orderId], refetchType: 'none' });
      void queryClient.invalidateQueries({ queryKey: ['exchangeStatuses'], refetchType: 'none' });
      refreshInBackground();
      await queryClient.invalidateQueries({ queryKey: ['saleOffersByBasis'] });
      notifications.show({ color: 'lime', message: t('app.sell.lot.deleted') });
    },
    onError,
  });
  const publishExchange = useMutation({
    mutationFn: (orderId: number) => sellService.publishToExchange(orderId),
    onSuccess: succeed('app.sell.exchange.published'),
    onError,
  });
  const removeExchange = useMutation({
    mutationFn: (orderId: number) => sellService.removeFromExchange(orderId),
    onSuccess: succeed('app.sell.exchange.removed'),
    onError,
  });

  // Идёт ли сейчас любое действие: пока оно не завершилось, остальные лоты карточки тоже не принимают действий, иначе
  // новое действие подменило бы отслеживаемый лот, и строка первого снова предложила бы то же действие.
  const anyPending =
    suspend.isPending || unsuspend.isPending || removeOffer.isPending || publishExchange.isPending || removeExchange.isPending;
  const pendingId = (orderId: number): boolean =>
    (suspend.isPending && suspend.variables === orderId) ||
    (unsuspend.isPending && unsuspend.variables === orderId) ||
    (removeOffer.isPending && removeOffer.variables === orderId) ||
    (publishExchange.isPending && publishExchange.variables === orderId) ||
    (removeExchange.isPending && removeExchange.variables === orderId);

  // `onDone` вызывается только при успехе и только пока вызвавший компонент смонтирован: колбэк уровня вызова mutate
  // после размонтирования не срабатывает. Страница предложения пересоздаётся при смене номера, поэтому переход после
  // удаления не уведёт пользователя ни из формы, ни с другого предложения.
  const confirmDeleteOffer = (orderId: number, onDone?: () => void): void => {
    modals.openConfirmModal({
      title: t('app.sell.lot.deleteTitle'),
      children: t('app.sell.lot.deleteConfirm'),
      labels: { confirm: t('app.sell.del.confirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => removeOffer.mutate(orderId, { onSuccess: onDone }),
    });
  };

  // Снятие с биржи требует подтверждения: оффер уходит из торгов, публикация заново — вручную.
  const confirmRemoveFromExchange = (orderId: number, onDone?: () => void): void => {
    modals.openConfirmModal({
      title: t('app.sell.exchange.removeTitle'),
      children: t('app.sell.exchange.removeConfirm'),
      labels: { confirm: t('app.sell.exchange.removeAction'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      // onDone — после ответа сервера при любом исходе (возврат фокуса на странице лота), как у остальных действий.
      onConfirm: () => removeExchange.mutate(orderId, { onSettled: onDone }),
    });
  };

  return {
    suspend,
    unsuspend,
    removeOffer,
    publishExchange,
    removeExchange,
    pendingId,
    anyPending,
    confirmDeleteOffer,
    confirmRemoveFromExchange,
  };
}

// Один оффер по id (деталь). Включён при валидном id.
export function useSaleOffer(orderId: number | null) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['saleOffer', orderId] as const,
    queryFn: ({ signal }) => sellService.getSaleOffer(orderId!, signal),
    enabled: isAuthenticated && orderId != null && !Number.isNaN(orderId),
    staleTime: 60_000,
  });
}

// Гейт агентского договора: для не-агента без подписи бэкенд кладёт AGENCY_AGREEMENT в otherMessages
// (GET /order/get_banners_data). Сигнал специфичен разделу «Продам» (в глобальный баннер не выводим).
// Пока данные не загрузились — НЕ блокируем (false), чтобы не мигать гейтом на каждом входе.
export function useAgencyAgreementMissed(): boolean {
  const { data } = useRequiredData();
  return data?.otherMessages.includes('AGENCY_AGREEMENT') ?? false;
}

// Единый обработчик «Создать оффер» с ограничениями (список и деталь группы зовут один и тот же).
// Приоритет: аттестация → агентский договор → форма. Аттестация — базовое требование, проверяем её
// первой (единообразно с баннерами и ранними возвратами формы SellOfferFormPage).
export function useStartSellOffer(): () => void {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const agencyMissed = useAgencyAgreementMissed();
  const { data: profile } = useProfile();

  return useCallback(() => {
    if (profile && profile.isVerified === false) {
      notifications.show({ color: 'yellow', message: t('app.sell.needAttestation') });
      navigate(ROUTES.APP_COMPANY_ACCREDITATION);
      return;
    }
    if (agencyMissed) {
      notifications.show({ color: 'yellow', message: t('app.sell.gate.text') });
      navigate(ROUTES.APP_COMPANY_AGENCY);
      return;
    }
    navigate(ROUTES.APP_SELL_NEW);
  }, [agencyMissed, profile, navigate, t]);
}
